import { useState } from 'react'
import { chairpersonService } from '@/services'
import { downloadBlob } from '@/utils'

export const useChairpersonLogin = () => {
  const [loadingLogin, setLoadingLogin] = useState(false)

  const login = async formData => {
    setLoadingLogin(true)
    try {
      const response = await chairpersonService.login(formData)
      return response?.data
    } finally {
      setLoadingLogin(false)
    }
  }
  return { loadingLogin, login }
}

export const useChairpersonLogout = () => {
  const [loadingLogout, setLoadingLogout] = useState(false)

  const logout = async () => {
    setLoadingLogout(true)
    try {
      const response = await chairpersonService.logout()
      return response?.data
    } finally {
      setLoadingLogout(false)
    }
  }
  return { loadingLogout, logout }
}

export const useGetChairperson = () => {
  const [chairperson, setChairperson] = useState({})
  const [loadingChairperson, setLoadingChairperson] = useState(false)

  const getChairperson = async () => {
    setLoadingChairperson(true)
    try {
      const response = await chairpersonService.getCurrentChairperson()
      setChairperson(response?.data)
    } catch (error) {
      const status = error?.response?.status
      if (status === 401) {
        setChairperson({})
        return
      }
      throw error
    } finally {
      setLoadingChairperson(false)
    }
  }

  return { chairperson, setChairperson, loadingChairperson, getChairperson }
}

export const useChairpersonResetPassword = () => {
  const [loadingResetPassword, setLoadingResetPassword] = useState(false)

  const resetPassword = async formData => {
    setLoadingResetPassword(true)
    try {
      const response = await chairpersonService.resetPassword(formData)
      return response?.data
    } finally {
      setLoadingResetPassword(false)
    }
  }
  return { loadingResetPassword, resetPassword }
}

export const useChairpersonRequestMagicLink = () => {
  const [loadingRequestMagicLink, setLoadingRequestMagicLink] = useState(false)

  const requestMagicLink = async formData => {
    setLoadingRequestMagicLink(true)
    try {
      const response = await chairpersonService.requestMagicLink(formData)
      return response
    } finally {
      setLoadingRequestMagicLink(false)
    }
  }
  return { loadingRequestMagicLink, requestMagicLink }
}

export const useGetUpcomingDrives = () => {
  const [upcomingDrives, setUpcomingDrives] = useState([])
  const [loadingUpcomingDrives, setLoadingUpcomingDrives] = useState(false)

  const getUpcomingDrives = async accountId => {
    if (!accountId) {
      setUpcomingDrives([])
      return
    }
    setLoadingUpcomingDrives(true)
    try {
      const response = await chairpersonService.getUpcomingDrives(accountId)
      setUpcomingDrives(response?.data ?? [])
    } finally {
      setLoadingUpcomingDrives(false)
    }
  }

  return { upcomingDrives, loadingUpcomingDrives, getUpcomingDrives }
}

const PAST_DRIVES_PAGE_SIZE = 3

export const useGetPastDrives = () => {
  const [pastDrives, setPastDrives] = useState([])
  const [pastDrivesTotal, setPastDrivesTotal] = useState(0)
  const [pastDrivesPage, setPastDrivesPage] = useState(1)
  const [loadingPastDrives, setLoadingPastDrives] = useState(false)
  const [loadingMorePastDrives, setLoadingMorePastDrives] = useState(false)

  const getPastDrives = async (accountId, page = 1) => {
    if (!accountId) {
      setPastDrives([])
      setPastDrivesTotal(0)
      setPastDrivesPage(1)
      return
    }
    const isLoadMore = page > 1
    isLoadMore ? setLoadingMorePastDrives(true) : setLoadingPastDrives(true)
    try {
      const response = await chairpersonService.getPastDrives(
        accountId,
        page,
        PAST_DRIVES_PAGE_SIZE
      )
      const drives = response?.data ?? []
      setPastDrives(prev => (isLoadMore ? [...prev, ...drives] : drives))
      setPastDrivesTotal(response?.record_count ?? 0)
      setPastDrivesPage(page)
    } finally {
      isLoadMore ? setLoadingMorePastDrives(false) : setLoadingPastDrives(false)
    }
  }

  const loadMorePastDrives = accountId =>
    getPastDrives(accountId, pastDrivesPage + 1)

  const hasMorePastDrives = pastDrives.length < pastDrivesTotal

  return {
    pastDrives,
    pastDrivesTotal,
    loadingPastDrives,
    loadingMorePastDrives,
    hasMorePastDrives,
    getPastDrives,
    loadMorePastDrives,
  }
}

const DRIVE_DONORS_PAGE_SIZE = 3

export const useGetDriveResults = () => {
  const [driveResults, setDriveResults] = useState(null)
  const [loadingDriveResults, setLoadingDriveResults] = useState(false)
  const [driveResultsError, setDriveResultsError] = useState(null)

  const getDriveResults = async (driveId, accountId) => {
    if (!driveId || !accountId) return
    setLoadingDriveResults(true)
    setDriveResultsError(null)
    try {
      const response = await chairpersonService.getDriveResults(
        driveId,
        accountId
      )
      if (response?.status === 'error' || !response?.data) {
        setDriveResults(null)
        setDriveResultsError({ response: { status: response?.status_code } })
        return
      }
      setDriveResults(response.data)
    } catch (error) {
      setDriveResults(null)
      setDriveResultsError(error)
    } finally {
      setLoadingDriveResults(false)
    }
  }

  return {
    driveResults,
    loadingDriveResults,
    driveResultsError,
    getDriveResults,
  }
}

export const useGetDriveDonors = () => {
  const [driveDonors, setDriveDonors] = useState([])
  const [driveDonorsTotal, setDriveDonorsTotal] = useState(0)
  const [loadingDriveDonors, setLoadingDriveDonors] = useState(false)
  const [showingAllDriveDonors, setShowingAllDriveDonors] = useState(false)

  const getDriveDonors = async (
    driveId,
    accountId,
    limit = DRIVE_DONORS_PAGE_SIZE
  ) => {
    if (!driveId || !accountId) return
    setLoadingDriveDonors(true)
    try {
      const response = await chairpersonService.getDriveDonors(
        driveId,
        accountId,
        limit
      )
      setDriveDonors(response?.data ?? [])
      setDriveDonorsTotal(response?.record_count ?? 0)
      setShowingAllDriveDonors(!limit)
    } finally {
      setLoadingDriveDonors(false)
    }
  }

  const viewAllDriveDonors = (driveId, accountId) =>
    getDriveDonors(driveId, accountId, 0)

  return {
    driveDonors,
    driveDonorsTotal,
    loadingDriveDonors,
    showingAllDriveDonors,
    getDriveDonors,
    viewAllDriveDonors,
  }
}

export const useExportDriveDonorsExcel = () => {
  const [loadingExportDonorsExcel, setLoadingExportDonorsExcel] =
    useState(false)
  const [exportDonorsExcelError, setExportDonorsExcelError] = useState(null)

  const exportDonorsExcel = async (driveId, accountId, fileName) => {
    setLoadingExportDonorsExcel(true)
    setExportDonorsExcelError(null)
    try {
      const blob = await chairpersonService.exportDriveDonorsExcel(
        driveId,
        accountId
      )
      downloadBlob(blob, fileName || 'drive-donors.xlsx')
    } catch (error) {
      setExportDonorsExcelError(error)
    } finally {
      setLoadingExportDonorsExcel(false)
    }
  }

  return {
    exportDonorsExcel,
    loadingExportDonorsExcel,
    exportDonorsExcelError,
  }
}

export const useExportDriveSummaryPdf = () => {
  const [loadingExportSummaryPdf, setLoadingExportSummaryPdf] = useState(false)
  const [exportSummaryPdfError, setExportSummaryPdfError] = useState(null)

  const exportSummaryPdf = async (driveId, accountId, fileName) => {
    setLoadingExportSummaryPdf(true)
    setExportSummaryPdfError(null)
    try {
      const blob = await chairpersonService.exportDriveSummaryPdf(
        driveId,
        accountId
      )
      downloadBlob(blob, fileName || 'drive-summary.pdf')
    } catch (error) {
      setExportSummaryPdfError(error)
    } finally {
      setLoadingExportSummaryPdf(false)
    }
  }

  return {
    exportSummaryPdf,
    loadingExportSummaryPdf,
    exportSummaryPdfError,
  }
}

export const useCancelChairpersonAppointment = () => {
  const [loadingCancelAppointment, setLoadingCancelAppointment] =
    useState(false)

  const cancelAppointment = async appointmentId => {
    setLoadingCancelAppointment(true)
    try {
      const response = await chairpersonService.cancelAppointment(appointmentId)
      return response
    } finally {
      setLoadingCancelAppointment(false)
    }
  }

  return { loadingCancelAppointment, cancelAppointment }
}

export const useGetDonorSchedule = () => {
  const [donorSchedule, setDonorSchedule] = useState([])
  const [loadingDonorSchedule, setLoadingDonorSchedule] = useState(false)
  const [donorScheduleError, setDonorScheduleError] = useState(null)

  const getDonorSchedule = async operationId => {
    if (!operationId) return
    setLoadingDonorSchedule(true)
    setDonorScheduleError(null)
    try {
      const response = await chairpersonService.getDonorSchedule(operationId)
      if (response?.status === 'error') {
        setDonorSchedule([])
        setDonorScheduleError({ response: { status: response?.status_code } })
        return
      }
      setDonorSchedule(response?.data ?? [])
    } catch (error) {
      setDonorSchedule([])
      setDonorScheduleError(error)
    } finally {
      setLoadingDonorSchedule(false)
    }
  }

  return {
    donorSchedule,
    loadingDonorSchedule,
    donorScheduleError,
    getDonorSchedule,
  }
}

export const useCheckDriveAccess = () => {
  const [hasDriveAccess, setHasDriveAccess] = useState(null)
  const [checkingDriveAccess, setCheckingDriveAccess] = useState(false)
  const [driveAccessError, setDriveAccessError] = useState(null)

  const checkDriveAccess = async driveId => {
    if (!driveId) return
    setCheckingDriveAccess(true)
    setDriveAccessError(null)
    setHasDriveAccess(null)
    try {
      const response = await chairpersonService.checkDriveAccess(driveId)
      if (response?.status === 'error') {
        setHasDriveAccess(false)
        setDriveAccessError({ response: { status: response?.status_code } })
        return
      }
      setHasDriveAccess(true)
    } catch (error) {
      setHasDriveAccess(false)
      setDriveAccessError(error)
    } finally {
      setCheckingDriveAccess(false)
    }
  }

  return {
    hasDriveAccess,
    checkingDriveAccess,
    driveAccessError,
    checkDriveAccess,
  }
}

const SEARCH_DONORS_DEFAULT_LIMIT = 5

export const useSearchDonors = () => {
  const [searchResults, setSearchResults] = useState([])
  const [loadingSearchDonors, setLoadingSearchDonors] = useState(false)

  const searchDonors = async (
    searchText,
    page = 1,
    limit = SEARCH_DONORS_DEFAULT_LIMIT,
    accountId = null
  ) => {
    if (!searchText?.trim()) {
      setSearchResults([])
      return { donors: [], total: 0, page: 1, limit }
    }
    setLoadingSearchDonors(true)
    try {
      const response = await chairpersonService.searchDonors(
        searchText.trim(),
        limit,
        page,
        accountId
      )
      const body = response?.data
      const payload = body?.data ?? body
      const donors = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body)
            ? body
            : []
      const total = payload?.total ?? body?.total ?? donors.length
      const currentPage = payload?.page ?? body?.page ?? page
      const currentLimit = payload?.limit ?? body?.limit ?? limit
      setSearchResults(donors)
      return {
        donors,
        total: Number(total) || 0,
        page: currentPage,
        limit: currentLimit,
      }
    } finally {
      setLoadingSearchDonors(false)
    }
  }

  return { searchResults, searchDonors, loadingSearchDonors }
}

export const useScheduleDonor = () => {
  const [loadingScheduleDonor, setLoadingScheduleDonor] = useState(false)

  const scheduleDonor = async (donorId, slotId, notifyDonor = false) => {
    setLoadingScheduleDonor(true)
    try {
      const response = await chairpersonService.scheduleDonor(
        donorId,
        slotId,
        notifyDonor
      )
      return response
    } finally {
      setLoadingScheduleDonor(false)
    }
  }

  return { scheduleDonor, loadingScheduleDonor }
}

export const useChairpersonScheduleGuest = () => {
  const [loadingScheduleGuest, setLoadingScheduleGuest] = useState(false)

  const scheduleGuest = async payload => {
    setLoadingScheduleGuest(true)
    try {
      const response = await chairpersonService.scheduleGuest(payload)
      return response
    } finally {
      setLoadingScheduleGuest(false)
    }
  }

  return { scheduleGuest, loadingScheduleGuest }
}
